import { Controller, useFormContext } from 'react-hook-form';
import { EntityChip, Icons, Label, RadioGroup } from '@silverfort/sf-components';
import { FC, SVGProps, useEffect, useState } from 'react';
import { RecipientType } from '../../types';
import { EnrollmentEmailFormValues } from '../../../../types/forms';
import { Entity, UserSession } from '../../../../../../api';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { isUserPermitted } from '../../../../../../TowerMenu/TowerMenuUtils';

const ENTITY_CHIP_MAX_DISPLAY = 2;

const RECIPIENT_RADIO_GROUP_OPTIONS = [
    { title: 'Unpaired users', value: 'unpaired' },
    { title: 'Groups & OUs', value: 'groupOU' }
];

export interface Props {
    selectedEntities: Entity[];
    userSession?: UserSession;
    onRecipientTypeChange: (selectedType: RecipientType) => void;
    onUnselectEntity: (entityGuid: string) => void;
}

export const RecipientSection: FC<Props> = ({
    selectedEntities,
    userSession,
    onRecipientTypeChange,
    onUnselectEntity
}) => {
    const { control } = useFormContext<EnrollmentEmailFormValues>();
    const [calculatedChipMaxDisplay, setCalculatedChipMaxDisplay] = useState<number>(ENTITY_CHIP_MAX_DISPLAY);
    const [calculatedChipWidth, setCalculatedChipWidth] = useState<number>(120);

    useEffect(() => {
        if (selectedEntities.length === 1) {
            setCalculatedChipMaxDisplay(ENTITY_CHIP_MAX_DISPLAY)
            setCalculatedChipWidth(240);
        } else if (selectedEntities.length === ENTITY_CHIP_MAX_DISPLAY) {
            setCalculatedChipMaxDisplay(ENTITY_CHIP_MAX_DISPLAY);
            setCalculatedChipWidth(120);
        } else if (selectedEntities.length > ENTITY_CHIP_MAX_DISPLAY) {
            if (selectedEntities[0].name.length > 10) {
                setCalculatedChipMaxDisplay(1);
                setCalculatedChipWidth(200);
            } else {
                setCalculatedChipMaxDisplay(ENTITY_CHIP_MAX_DISPLAY);
                setCalculatedChipWidth(100);
            }
        }
    }, [selectedEntities]);

    return (
        <div className="h-8 flex flex-row gap-16 items-center px-4 flex-1">
            <Label text="Recipient" />
            <div className="flex flex-row gap-2">
                <Controller
                    name="recipientType"
                    control={control}
                    render={({ field: { value } }) => (
                        <RadioGroup
                            containerClassName="flex-shrink-0"
                            selectedOption={value}
                            options={RECIPIENT_RADIO_GROUP_OPTIONS.map((option) => {
                                if (option.value === 'groupOU') {
                                    return {
                                        ...option,
                                        disabled: !isUserPermitted(
                                            userSession,
                                            'sendEnrollmentEmail',
                                            ROLES.EDITOR.value
                                        )
                                    };
                                }
                                if (option.value === 'unpaired') {
                                    return {
                                        ...option,
                                        disabled: !isUserPermitted(
                                            userSession,
                                            'sendEnrollmentEmailToAllUnpaired',
                                            ROLES.VIEWER.value
                                        )
                                    };
                                }
                                return option;
                            })}
                            onChange={(value) => {
                                onRecipientTypeChange(value as RecipientType);
                            }}
                        />
                    )}
                />

                <div className="flex flex-row gap-2">
                    {selectedEntities.slice(0, calculatedChipMaxDisplay).map((entity, index) => (
                        <div key={`${entity.guid}_${index}`}>
                            <EntityChip
                                icon={Icons.UserGroup as FC<SVGProps<SVGSVGElement>>}
                                removable
                                text={entity.name}
                                onRemove={() => onUnselectEntity(entity.guid)}
                                className={`max-w-[${calculatedChipWidth}px]`}
                            />
                        </div>
                    ))}
                    {selectedEntities.length > calculatedChipMaxDisplay && (
                        <EntityChip
                            icon={Icons.Add as FC<SVGProps<SVGSVGElement>>}
                            text={selectedEntities.length - calculatedChipMaxDisplay}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
